import React, {useMemo,} from 'react';
import { useQuery } from "@tanstack/react-query";
import {getAdminSupplierReviewDetail, getReviewOptions} from "../../../../services/reviewService";
import RatingStars from '../RatingStars';
import ReviewOptionGroup from '../ReviewOptionGroup';
import EntityInfoGrid from '../EntityInfoGrid';
import {formatDate} from "../../../../utils/jalaliDate"
import {FiTruck, FiUserCheck} from "react-icons/fi";

import Button from "../../../Button";

const getReviewId = (reviewData) => {
  return reviewData?.id || reviewData?.review_id || reviewData?.review?.id || null;
};



const pick = (...values) => {
  return values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );
};

const buildEntityInfoItems = (entity) => {
  if (!entity) return [];

  if (Array.isArray(entity.infoItems)) {
    return entity.infoItems;
  }

  const entityType = entity.entityType || entity.entity_type;
  const shippingLabels = {
    carrier: { label: "ارسال شرکت حمل", icon: <FiTruck className="w-5 h-5" /> },
    supplier: { label: "ارسال تأمین‌کننده", icon: <FiUserCheck className="w-5 h-5" /> },
    pickup: { label: "تحویل حضوری", icon: <FiUserCheck className="w-5 h-5" /> },
  };

  const UNIT_TRANSLATIONS = {
    'count': 'عدد',
    'package': 'بسته',
    'weight': 'کیلو گرم',
    'length': 'متر',
    'area': 'متر مربع',
    'litr': 'لیتر',
    'volume': 'متر مکعب'
    };
  const info = entity.info || {};

  if (entityType === "shipment") {
    return [
      {
        label: "کد محموله",
        value: pick(info.shipment_code?.slice(0, 8)),
      },
      {
        label: "زمان ثبت سفارش",
        value: pick(formatDate(info.created_at)),
      },
      {
        label: "زمان تحویل",
        value: pick(formatDate(info.delivered_at)),
      },
    {
        label: "تأمین کننده",
        value: pick(info.supplier_name),
      }
    ].filter((item) => item.value);
  }

  if (entityType === "shipment_item") {
    return [
      {
        label: "نام کالا",
        value: pick(info.product_name),
      },
      {
        label: "دسته کالا",
        value: pick(info.product_category),
      },
      {
        label: "برند کالا",
        value: pick(info.product_brand),
      },
      {
        label: "تعداد",
        value: pick(info.qty_ordered),
      }
    ].filter((item) => item.value);
  }

  return [];
};

const buildEntityImages = (entity) => {
  if (!entity) return [];

  const info = entity.info || {};

  const images = [
    info.product_image,
    info.image,
    info.thumbnail,
  ].filter(Boolean);

  return images;
};


const AdminReviewViewModal = ({
  open,
  onClose,
  entity,
  reviewData,
}) => {

  const reviewId = getReviewId(reviewData);
  const entityType = entity?.entityType || entity?.entity_type;

  const ratingLabels = {
    1: "خیلی ضعیف",
    2: "ضعیف",
    3: "متوسط",
    4: "خوب",
    5: "عالی",
  };

  const entityInfoItems = useMemo(
    () => buildEntityInfoItems(entity),
    [entity]
  );

  const entityImages = useMemo(
    () => buildEntityImages(entity),
    [entity]
  );
  const { data: optionsData } = useQuery({
    queryKey: ["review-options", entityType],
    queryFn: () => getReviewOptions(entityType),
    enabled: open && !!entityType,
    staleTime: 1000 * 60 * 10,
  });

  const options = useMemo(() => {
    if (Array.isArray(optionsData)) return optionsData;
    if (Array.isArray(optionsData?.results)) return optionsData.results;
    return [];
  }, [optionsData]);

  const positiveOptionsList = options.filter(
    (o) => o.sentiment === "positive"
  );

  const negativeOptionsList = options.filter(
    (o) => o.sentiment === "negative"
  );

  const {
    data: review,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["review-detail", reviewId],
    queryFn: () => getAdminSupplierReviewDetail(reviewId),
    enabled: open && Boolean(reviewId),
  });

   if (!open) return null;

  if (!reviewId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="bg-white p-6 rounded shadow text-red-600">
          شناسه نظر برای نمایش وجود ندارد.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white p-6 rounded shadow">در حال بارگذاری...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="bg-white p-6 rounded shadow text-red-600">
          {error?.response?.data?.detail || "خطا در دریافت اطلاعات نظر"}
        </div>
      </div>
    );
  }


  const positiveOptions = review?.selected_options?.filter((x) => x.sentiment === 'positive') || [];
  const negativeOptions = review?.selected_options?.filter((x) => x.sentiment === 'negative') || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-5xl bg-white border rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="p-8 rtl">
          <div className="flex justify-between items-center mb-6 border-b pb-4">

            <h2 className="text-xl font-bold">
              مشاهده نظر ثبت شده.
            </h2>
            {!review?.is_active && (<div className='rounded-[8px] text-red-600 bg-red-50 text-[14px] p-2'>غیر فعال</div>)}

            <button
                type="button"
                onClick={onClose}
                className="text-xl text-gray-500"
            >
              ×
            </button>
          </div>
          <EntityInfoGrid items={entityInfoItems}/>

          {entityImages.length > 0 && (
            <div className="flex gap-3 mt-6">
              {entityImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt=""
                  className="w-16 h-16 rounded object-cover border"
                />
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-col items-center">
            <p className="mb-3 text-sm">
              امتیاز شما :
              {review?.rating > 0 && (
                  <span className="text-gray-600 font-medium">
                    {ratingLabels[review?.rating]}
                  </span>
              )}
            </p>
            <RatingStars value={review?.rating || 0} readOnly/>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-10">
            <ReviewOptionGroup
                title="نقاط قوت"
                variant="positive"
                options={positiveOptionsList}
                selectedIds={positiveOptions.map((x) => x.id)}
                readOnly
            />

            <ReviewOptionGroup
                title="نقاط ضعف"
                variant="negative"
                options={negativeOptionsList}
                selectedIds={negativeOptions.map((x) => x.id)}
                readOnly
            />
          </div>

          <div className="mt-10">
            <textarea
                value={review?.comment || ''}
                readOnly
                rows={5}
                className="w-full border rounded-md p-4 text-sm bg-gray-50 rtl text-right"
            />
          </div>

          <div className="mt-6 flex gap-2 justify-start">
          <Button
                variant={'secondary'}
                children={'بستن'}
                onClick={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReviewViewModal;
